import { PriceAlert } from './domain';
import { lookitPriceAlerts } from './mock-data';
import { LookitStorage } from './saved-item-service';

export interface AlertServiceOptions {
    storageKey?: string;
    storage?: LookitStorage | null;
    userId?: string | null;
}

export const priceAlertsStorageKey = 'lookit.price-alerts.v2';
export const priceAlertsLegacyStorageKeys = ['lookit.price-alerts.v1', 'lookit.price-alerts'];

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function browserStorage(storage?: LookitStorage | null): LookitStorage | null {
    if (storage !== undefined) return storage;
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
}

function normalizePriceAlerts(items: PriceAlert[]): PriceAlert[] {
    return items
        .filter((alert) => Boolean(alert.product_cluster_id))
        .map((alert) => ({
            ...alert,
            listing_id: alert.listing_id || null,
            listing_option_id: alert.listing_option_id || null,
            alert_scope: alert.alert_scope || (alert.listing_option_id || alert.alert_type === 'restock' ? 'listing_option' : alert.listing_id ? 'listing' : 'product_cluster'),
            saved_item_id: alert.saved_item_id || null,
            selected_category_id: alert.selected_category_id || null,
            selected_category_label: alert.selected_category_label || alert.baseline_category_label || null,
            selected_region_label: alert.selected_region_label || alert.baseline_region_label || null,
            selected_region: alert.selected_region || null,
            crop_image_id: alert.crop_image_id || null,
            original_image_size: alert.original_image_size || null,
            displayed_image_size: alert.displayed_image_size || null,
            crop_region_original: alert.crop_region_original || null,
            crop_preview_url: alert.crop_preview_url || null,
            local_crop_id: alert.local_crop_id || null,
            source_request_id: alert.source_request_id || null,
            baseline_product_name: alert.baseline_product_name || null,
            baseline_image_url: alert.baseline_image_url || null,
            baseline_category_label: alert.baseline_category_label || alert.selected_category_label || null,
            baseline_region_label: alert.baseline_region_label || alert.selected_region_label || null,
            target_color: alert.target_color || null,
            target_size: alert.target_size || null
        }));
}

function filterByUserId(alerts: PriceAlert[], userId?: string | null): PriceAlert[] {
    return userId ? alerts.filter((alert) => alert.user_id === userId) : alerts;
}

function shouldPersistNormalizedAlerts(items: PriceAlert[], normalizedAlerts: PriceAlert[]): boolean {
    return JSON.stringify(items) !== JSON.stringify(normalizedAlerts);
}

function parseStoredArray<T extends object>(rawValue: string | null): T[] | null {
    if (rawValue === null) return null;

    try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
        return null;
    }
}

function readLocalArray<T extends object>(storageKey: string, fallback: T[], storage?: LookitStorage | null, legacyStorageKeys: string[] = []): T[] {
    const fallbackItems = cloneData(fallback);
    const resolvedStorage = browserStorage(storage);

    if (!resolvedStorage) return fallbackItems;

    const currentItems = parseStoredArray<T>(resolvedStorage.getItem(storageKey));
    if (currentItems) return currentItems;

    for (const legacyKey of legacyStorageKeys) {
        const legacyItems = parseStoredArray<T>(resolvedStorage.getItem(legacyKey));
        if (legacyItems) {
            resolvedStorage.setItem(storageKey, JSON.stringify(legacyItems));
            return legacyItems;
        }
    }

    resolvedStorage.setItem(storageKey, JSON.stringify(fallbackItems));
    return fallbackItems;
}

export async function listPriceAlerts(options: AlertServiceOptions = {}): Promise<PriceAlert[]> {
    const storageKey = options.storageKey || priceAlertsStorageKey;
    const alerts = readLocalArray<PriceAlert>(storageKey, lookitPriceAlerts, options.storage, priceAlertsLegacyStorageKeys);
    const normalizedAlerts = normalizePriceAlerts(alerts);
    const resolvedStorage = browserStorage(options.storage);

    if (resolvedStorage && shouldPersistNormalizedAlerts(alerts, normalizedAlerts)) {
        resolvedStorage.setItem(storageKey, JSON.stringify(normalizedAlerts));
    }

    return filterByUserId(normalizedAlerts, options.userId);
}

export async function savePriceAlerts(alerts: PriceAlert[], options: AlertServiceOptions = {}): Promise<PriceAlert[]> {
    const storageKey = options.storageKey || priceAlertsStorageKey;
    const normalizedAlerts = normalizePriceAlerts(cloneData(alerts));
    const resolvedStorage = browserStorage(options.storage);
    const nextAlerts = options.userId
        ? [
            ...normalizedAlerts,
            ...normalizePriceAlerts(readLocalArray<PriceAlert>(storageKey, lookitPriceAlerts, options.storage, priceAlertsLegacyStorageKeys))
                .filter((alert) => alert.user_id !== options.userId)
        ]
        : normalizedAlerts;

    if (resolvedStorage) {
        resolvedStorage.setItem(storageKey, JSON.stringify(nextAlerts));
    }

    return cloneData(filterByUserId(nextAlerts, options.userId));
}

export const alertService = {
    storageKey: priceAlertsStorageKey,
    legacyStorageKeys: priceAlertsLegacyStorageKeys,
    listPriceAlerts,
    savePriceAlerts
};
